import { DashboardLayout } from "../../components/dashboard-layout";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../theme";
import { 
    Box, 
    Container, 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableRow, 
    Pagination, 
    TextField, 
    Button, 
    Select, 
    MenuItem, 
    FormControl, 
    Typography,
    Card,
    Chip,
    InputAdornment,
    Alert
} from "@mui/material";
import axios from "axios";
import { useEffect, useState, forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import React from "react";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import LanIcon from "@mui/icons-material/Lan";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const CustomDateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
    <Box
        ref={ref}
        onClick={onClick}
        sx={{
            height: "40px",
            padding: "0 12px",
            borderRadius: "6px",
            border: "1px solid #cbd5e1",
            fontSize: "14px",
            color: value ? "#0f172a" : "#94a3b8",
            backgroundColor: "#ffffff",
            width: "140px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
                borderColor: "#3b82f6"
            }
        }}
    >
        <span>{value || placeholder || "dd/mm/yyyy"}</span>
        <CalendarMonthIcon sx={{ fontSize: 18, color: "#64748b" }} />
    </Box>
));

function LoginHistory() {
    function formatDate(m) {
        if (!m) return "-";
        const d = new Date(m);
        const day = ("0" + d.getDate()).slice(-2);
        const month = ("0" + (d.getMonth() + 1)).slice(-2);
        const year = d.getFullYear();
        const hours = ("0" + d.getHours()).slice(-2);
        const minutes = ("0" + d.getMinutes()).slice(-2);
        const seconds = ("0" + d.getSeconds()).slice(-2);
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    }

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);

    const [usernameInput, setUsernameInput] = useState("");
    const [ipInput, setIpInput] = useState("");
    const [roleInput, setRoleInput] = useState("");
    const [searchedUsername, setSearchedUsername] = useState("");
    const [searchedIp, setSearchedIp] = useState("");
    const [searchedRole, setSearchedRole] = useState("");

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const limit = 15;

    axios.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem("user");
            if (token) {
                config.headers["Authorization"] = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    const formatLocalDate = (date) => {
        if (!date) return "";
        const offset = date.getTimezoneOffset();
        const localDate = new Date(date.getTime() - (offset * 60 * 1000));
        return localDate.toISOString().split('T')[0];
    };

    const profile = JSON.parse(localStorage.getItem("profile") || "{}");
    const perm = profile?.permission || {};
    const isFull = perm.full == 1 || perm.full === true || profile?.iduser === 1;
    const canView = isFull || Number(perm.site_login_logs) === 1 || perm.site_login_logs === true ||
        (perm.site_login_logs === undefined && (Number(perm.logs) === 1 || perm.logs === true));

    const fetchLoginLogs = () => {
        if (!canView) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const startStr = formatLocalDate(startDate);
        const endStr = formatLocalDate(endDate);
        axios
            .get(
                `${process.env.REACT_APP_API_URL}/statistic/login-history?page=${page}&limit=${limit}&username=${searchedUsername}&ipAddress=${searchedIp}&role=${searchedRole}&startDate=${startStr}&endDate=${endStr}`
            )
            .then((res) => {
                if (res.data?.success === false) {
                    setData([]);
                    setTotalLogs(0);
                    setTotalPages(1);
                    setLoading(false);
                    return;
                }
                setData(res.data.data);
                if (res.data.totalPages) {
                    setTotalPages(res.data.totalPages);
                }
                if (res.data.total !== undefined) {
                    setTotalLogs(res.data.total);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("Fetch login history error:", err);
                setLoading(false);
            });
    };

    useEffect(() => {
        if (canView) {
            fetchLoginLogs();
        }
    }, [page, searchedUsername, searchedIp, searchedRole, startDate, endDate]);

    const handleSearch = () => {
        setSearchedUsername(usernameInput);
        setSearchedIp(ipInput);
        setRoleInput(roleInput);
        setPage(1);
    };

    const handleReset = () => {
        setUsernameInput("");
        setIpInput("");
        setRoleInput("");
        setSearchedUsername("");
        setSearchedIp("");
        setSearchedRole("");
        setStartDate(null);
        setEndDate(null);
        setPage(1);
    };

    const handleChangePage = (e, newPage) => {
        setPage(newPage);
    };

    const renderRoleBadge = (role) => {
        switch (role) {
            case "admin":
                return (
                    <Chip 
                        label="Admin" 
                        size="small" 
                        sx={{ 
                            backgroundColor: "#fef2f2", 
                            color: "#dc2626", 
                            fontWeight: 700, 
                            border: "1px solid #fca5a5",
                            borderRadius: "6px"
                        }} 
                    />
                );
            case "customer":
                return (
                    <Chip 
                        label="Nhân viên" 
                        size="small" 
                        sx={{ 
                            backgroundColor: "#f0f9ff", 
                            color: "#0284c7", 
                            fontWeight: 700, 
                            border: "1px solid #bae6fd",
                            borderRadius: "6px"
                        }} 
                    />
                );
            default:
                return (
                    <Chip 
                        label="Người chơi" 
                        size="small" 
                        sx={{ 
                            backgroundColor: "#f1f5f9", 
                            color: "#475569", 
                            fontWeight: 600, 
                            border: "1px solid #cbd5e1",
                            borderRadius: "6px"
                        }} 
                    />
                );
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <DashboardLayout>
                <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    {loading && (
                        <div className="loading" style={{ maxWidth: "100%", zIndex: 9999 }}>
                            <div className="loader"></div>
                        </div>
                    )}
                    <Container maxWidth={false}>
                        {/* Page Header */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1.5, color: "#0f172a" }}>
                                <HistoryRoundedIcon sx={{ fontSize: 32, color: "#2563eb" }} /> Nhật Ký Đăng Nhập
                            </Typography>
                            {canView && (
                                <Chip 
                                    label={`Tổng cộng: ${totalLogs} bản ghi`} 
                                    color="primary" 
                                    variant="outlined" 
                                    sx={{ fontWeight: 600, fontSize: "14px", py: 0.5 }} 
                                />
                            )}
                        </Box>

                        {!canView ? (
                            <Alert severity="error" sx={{ mt: 3, borderRadius: 2, fontSize: "15px", fontWeight: 600 }}>
                                Bạn không có quyền truy cập trang Nhật Ký Đăng Nhập. Vui lòng liên hệ quản trị viên để được cấp quyền.
                            </Alert>
                        ) : (
                            <>
                        {/* Filter Card */}
                        <Card sx={{ p: 2.5, mb: 3, borderRadius: 2, boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)", backgroundColor: "#ffffff" }}>
                            <Box className="happy-login-filter-row" sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                                <TextField
                                    size="small"
                                    value={usernameInput}
                                    onChange={(e) => setUsernameInput(e.target.value)}
                                    placeholder="Tên người dùng"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonSearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ minWidth: 200, "& .MuiInputBase-root": { height: "40px" } }}
                                />

                                <TextField
                                    size="small"
                                    value={ipInput}
                                    onChange={(e) => setIpInput(e.target.value)}
                                    placeholder="Địa chỉ IP"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LanIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ minWidth: 180, "& .MuiInputBase-root": { height: "40px" } }}
                                />

                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <Select
                                        value={roleInput}
                                        onChange={(e) => setRoleInput(e.target.value)}
                                        displayEmpty
                                        sx={{ height: "40px", fontSize: "14px" }}
                                    >
                                        <MenuItem value="">Tất cả vai trò</MenuItem>
                                        <MenuItem value="user">Người chơi</MenuItem>
                                        <MenuItem value="admin">Admin</MenuItem>
                                        <MenuItem value="customer">Nhân viên</MenuItem>
                                    </Select>
                                </FormControl>

                                <Box className="happy-login-date-box" sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569" }}>Từ ngày:</Typography>
                                    <DatePicker
                                        maxDate={new Date()}
                                        selected={startDate}
                                        onChange={(date) => {
                                            setStartDate(date);
                                            setPage(1);
                                        }}
                                        customInput={<CustomDateInput placeholder="Bắt đầu" />}
                                    />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", ml: 1 }}>Đến ngày:</Typography>
                                    <DatePicker
                                        maxDate={new Date()}
                                        selected={endDate}
                                        onChange={(date) => {
                                            setEndDate(date);
                                            setPage(1);
                                        }}
                                        customInput={<CustomDateInput placeholder="Kết thúc" />}
                                    />
                                </Box>

                                <Box className="happy-login-filter-actions" sx={{ display: "flex", gap: 1.5, ml: "auto" }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<SearchIcon />}
                                        onClick={handleSearch}
                                        sx={{ 
                                            height: "40px", 
                                            textTransform: "none", 
                                            fontWeight: 600,
                                            backgroundColor: "#2563eb", 
                                            "&:hover": { backgroundColor: "#1d4ed8" } 
                                        }}
                                    >
                                        Tìm kiếm
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RestartAltIcon />}
                                        onClick={handleReset}
                                        sx={{ 
                                            height: "40px", 
                                            textTransform: "none", 
                                            fontWeight: 600,
                                            borderColor: "#cbd5e1", 
                                            color: "#475569", 
                                            "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" } 
                                        }}
                                    >
                                        Cài lại
                                    </Button>
                                </Box>
                            </Box>
                        </Card>

                        {/* Data Table Card */}
                        <Card sx={{ borderRadius: 2, boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)", overflowX: "auto" }}>
                            <Table sx={{ minWidth: 700 }}>
                                <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, color: "#334155", py: 2 }}>#</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "#334155", py: 2 }}>Tên người dùng</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "#334155", py: 2 }}>Vai trò</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "#334155", py: 2 }}>Địa chỉ IP</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "#334155", py: 2 }}>Thiết bị / Trình duyệt</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "#334155", py: 2 }}>Thời gian đăng nhập</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data != null && data.length > 0 ? (
                                        data.map((item, idx) => (
                                            <TableRow 
                                                key={item._id}
                                                sx={{ 
                                                    "&:hover": { backgroundColor: "#f8fafc" },
                                                    transition: "background-color 0.15s ease"
                                                }}
                                            >
                                                <TableCell sx={{ py: 1.8, color: "#64748b" }}>
                                                    {(page - 1) * limit + idx + 1}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.8, color: "#2563eb", fontWeight: 700 }}>
                                                    {item.username}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.8 }}>
                                                    {renderRoleBadge(item.role)}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.8 }}>
                                                    <Box sx={{
                                                        display: "inline-block",
                                                        fontFamily: "monospace",
                                                        fontSize: "13px",
                                                        fontWeight: 600,
                                                        backgroundColor: "#f1f5f9",
                                                        color: "#334155",
                                                        px: 1.2,
                                                        py: 0.4,
                                                        borderRadius: "4px",
                                                        border: "1px solid #e2e8f0"
                                                    }}>
                                                        {item.ipAddress || "-"}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ py: 1.8, color: "#475569", maxWidth: 300, wordBreak: "break-word" }}>
                                                    {item.device || "-"}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.8, color: "#334155", fontWeight: 500 }}>
                                                    {formatDate(item.createdAt)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 6, color: "#94a3b8", fontSize: "15px" }}>
                                                Không tìm thấy lịch sử đăng nhập nào
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Card>

                        {/* Pagination */}
                        {data != null && data.length > 0 && (
                            <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>
                                <Pagination 
                                    color="primary" 
                                    page={page} 
                                    count={totalPages} 
                                    shape="rounded" 
                                    onChange={handleChangePage} 
                                />
                            </Box>
                        )}
                            </>
                        )}
                    </Container>
                </Box>
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default LoginHistory;
